import type { ClubTeamsDivisionSchemaClient } from "@teamz/common";

import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clubTeamsDivisionSchemaClient, deDivisionsList, frDivisionsList, itDivisionsList } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useUserRole } from "@/hooks/use-user-role";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const divisions = ["firstDivision", "secondDivision", "thirdDivision"] as const;

type ClubType = RouterOutputs["club"]["getClub"];

type DivisionProps = {
  division: "firstDivision" | "secondDivision" | "thirdDivision";
  firstDivisionValue: string | undefined;
  secondDivisionValue: string | undefined;
  form: ReturnType<typeof useForm>;
};

const isSelectDisabled = (division: DivisionProps["division"], firstDivisionValue: DivisionProps["firstDivisionValue"], secondDivisionValue: DivisionProps["secondDivisionValue"]) => {
  switch (division) {
    case "secondDivision":
      return !firstDivisionValue;
    case "thirdDivision":
      return !secondDivisionValue;
    default:
      return false;
  }
};

const DivisionSelect = ({ division, form, club, firstDivisionValue, secondDivisionValue, genre }: { division: DivisionProps["division"]; form: DivisionProps["form"]; club: ClubType; firstDivisionValue: DivisionProps["firstDivisionValue"]; secondDivisionValue: DivisionProps["secondDivisionValue"]; genre: "men" | "women" }) => {
  const { t } = useTranslation(["profile"]);

  return (
    <Controller
      key={division}
      control={form.control}
      name={division}
      render={({ field, fieldState }) => (
        <RootField>
          <Label>{t(`profile:${division}`)}</Label>
          <Select value={field.value} onValueChange={field.onChange} disabled={isSelectDisabled(division, firstDivisionValue, secondDivisionValue)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {
                  (club.clubCountryCode === "fr" || club.clubCountryCode === "re") && (
                    frDivisionsList.filter(label => (
                      genre === "women" ? label !== "Nationale 4" : label
                    )).map(label => (
                      <SelectItem key={label} value={label}>
                        {label}
                      </SelectItem>
                    ))
                  )
                }
                {
                  club.clubCountryCode === "it" && (
                    itDivisionsList.map(label => (
                      <SelectItem key={label} value={label}>
                        {label}
                      </SelectItem>
                    ))
                  )
                }
                {
                  club.clubCountryCode === "de" && (
                    deDivisionsList.map(label => (
                      <SelectItem key={label} value={label}>
                        {label}
                      </SelectItem>
                    ))
                  )
                }
              </SelectGroup>
            </SelectContent>
          </Select>
          {fieldState.error?.message && (
            <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
          )}
        </RootField>
      )}
    />
  );
};

function MenTeamForm({ club, closeModal }: { club: ClubType; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateMenTeams.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const menTeamForm = useForm<ClubTeamsDivisionSchemaClient>({
    resolver: zodResolver(clubTeamsDivisionSchemaClient),
    defaultValues: {
      firstDivision: club.clubTeamsMenDivision?.[0] || undefined,
      secondDivision: club.clubTeamsMenDivision?.[1] || undefined,
      thirdDivision: club.clubTeamsMenDivision?.[2] || undefined,
    },
  });

  const onSubmit = menTeamForm.handleSubmit((data) => {
    const arr = Object.values(data).filter(Boolean);

    mutate({
      clubRepresentativeId: club.clubRepresentativeId,
      clubTeamsMenDivision: arr,
    });
  });

  const onClear = () => {
    mutate({
      clubRepresentativeId: club.clubRepresentativeId,
      clubTeamsMenDivision: [],
    });
  };

  const firstDivisionValue = menTeamForm.watch("firstDivision");
  const secondDivisionValue = menTeamForm.watch("secondDivision");

  return (
    <>
      <SheetForm id="MenTeamForm" onSubmit={onSubmit}>
        {
          divisions.map(division => (
            ["fr", "re", "it", "de"].includes(club.clubCountryCode)
              ? (
                  <DivisionSelect
                    key={division}
                    genre="men"
                    division={division}
                    form={menTeamForm}
                    club={club}
                    firstDivisionValue={firstDivisionValue}
                    secondDivisionValue={secondDivisionValue}
                  />
                )
              : (
                  <TextField
                    key={division}
                    {...menTeamForm.register(division)}
                    label={t(`profile:${division}`)}
                    disabled={isSelectDisabled(division, firstDivisionValue, secondDivisionValue)}
                    errorMessage={tDyn(t, menTeamForm.formState.errors[division]?.message)}
                  />
                )
          ))
        }
      </SheetForm>

      { !!club.clubTeamsMenDivision?.length && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-teams")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="MenTeamForm" loading={isPending || menTeamForm.formState.isSubmitting} disabled={menTeamForm.watch("firstDivision") === undefined || menTeamForm.watch("firstDivision") === ""}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function WomenTeamForm({ club, closeModal }: { club: ClubType; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateWomenTeams.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const womenTeamForm = useForm<ClubTeamsDivisionSchemaClient>({
    resolver: zodResolver(clubTeamsDivisionSchemaClient),
    defaultValues: {
      firstDivision: club.clubTeamsWomenDivision?.[0] || undefined,
      secondDivision: club.clubTeamsWomenDivision?.[1] || undefined,
      thirdDivision: club.clubTeamsWomenDivision?.[2] || undefined,
    },
  });

  const onSubmit = womenTeamForm.handleSubmit((data) => {
    const arr = Object.values(data).filter(Boolean);

    mutate({
      clubRepresentativeId: club.clubRepresentativeId,
      clubTeamsWomenDivision: arr,
    });
  });

  const onClear = () => {
    mutate({
      clubRepresentativeId: club.clubRepresentativeId,
      clubTeamsWomenDivision: [],
    });
  };

  const firstDivisionValue = womenTeamForm.watch("firstDivision");
  const secondDivisionValue = womenTeamForm.watch("secondDivision");

  return (
    <>
      <SheetForm id="WomenTeamForm" onSubmit={onSubmit}>
        {
          divisions.map(division => (
            ["fr", "re", "it", "de"].includes(club.clubCountryCode)
              ? (
                  <DivisionSelect
                    key={division}
                    genre="women"
                    division={division}
                    form={womenTeamForm}
                    club={club}
                    firstDivisionValue={firstDivisionValue}
                    secondDivisionValue={secondDivisionValue}
                  />
                )
              : (
                  <TextField
                    key={division}
                    {...womenTeamForm.register(division)}
                    label={t(`profile:${division}`)}
                    disabled={isSelectDisabled(division, firstDivisionValue, secondDivisionValue)}
                    errorMessage={tDyn(t, womenTeamForm.formState.errors[division]?.message)}
                  />
                )
          ))
        }
      </SheetForm>

      { !!club.clubTeamsWomenDivision?.length && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-teams")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="WomenTeamForm" loading={isPending || womenTeamForm.formState.isSubmitting} disabled={womenTeamForm.watch("firstDivision") === undefined || womenTeamForm.watch("firstDivision") === ""}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function ClubTeams({ club }: { club: ClubType }) {
  const [modalState, updateModalState] = useState<"men" | "women" | "close">("close");
  const isModalOpen = modalState !== "close";
  const { t } = useTranslation(["common", "profile"]);
  const userRole = useUserRole();
  const { user } = useMe();

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">
          {t("common:teams")}
        </h3>

        <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
          <button
            type="button"
            className={cn("flex h-14 items-center rounded-xl bg-neutral-50", userRole === "player" && !user.isAdmin && "pointer-events-none")}
            onClick={() => updateModalState("men")}
          >
            <div className="px-2">
              <Badge variant="blue" size="compact" className="shrink-0 text-sm">{t("common:men").charAt(0)}</Badge>
            </div>

            <div className="flex h-full flex-1 items-center gap-2 p-1">
              {
                [0, 1, 2].map(key => (
                  <div key={key} className="flex h-full flex-1 items-center justify-center rounded-lg bg-white px-2">
                    {
                      club.clubTeamsMenDivision?.length && club.clubTeamsMenDivision[key]
                        ? (
                            <span className="line-clamp-2 text-balance text-center text-sm font-semibold">{club.clubTeamsMenDivision[key]}</span>
                          )
                        : (
                            <DotsIcon />
                          )
                    }
                  </div>
                ))
              }
            </div>

            {(userRole !== "player" || user.isAdmin) && (
              <div className="shrink-0 px-1">
                <EditIcon className="size-4 text-neutral-500" />
              </div>
            )}
          </button>

          <button
            type="button"
            className={cn("flex h-14 items-center rounded-lg bg-neutral-50", userRole === "player" && !user.isAdmin && "pointer-events-none")}
            onClick={() => updateModalState("women")}
          >
            <div className="px-2">
              <Badge variant="purple" size="compact" className="shrink-0 text-sm">{t("common:women").charAt(0)}</Badge>
            </div>

            <div className="flex h-full flex-1 items-center gap-2 p-1">
              {
                [0, 1, 2].map(key => (
                  <div key={key} className="flex h-full flex-1 items-center justify-center rounded-lg bg-white px-2">
                    {
                      club.clubTeamsWomenDivision?.length && club.clubTeamsWomenDivision[key]
                        ? (
                            <span className="line-clamp-2 text-balance text-center text-sm font-semibold">{club.clubTeamsWomenDivision[key]}</span>
                          )
                        : (
                            <DotsIcon />
                          )
                    }
                  </div>
                ))
              }
            </div>

            { (userRole !== "player" || user.isAdmin) && (
              <div className="shrink-0 px-1">
                <EditIcon className="size-4 text-neutral-500" />
              </div>
            )}
          </button>
        </div>
      </div>

      { (userRole !== "player" || user.isAdmin) && (
        <Sheet open={isModalOpen} onOpenChange={() => updateModalState("close")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{modalState !== "close" && `${t("profile:teams-gender")} ${t(`common:${modalState}`)}`}</SheetTitle>
            </SheetHeader>

            {modalState === "men" && <MenTeamForm club={club} closeModal={() => updateModalState("close")} />}
            {modalState === "women" && <WomenTeamForm club={club} closeModal={() => updateModalState("close")} />}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
